import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Col, Label, Loading } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';

import {
  AccountAddress,
  useAccountAddressesQuery,
} from '../../hooks/queries/useAccountAddressesQuery';
import {
  Facility,
  useFacilitiesQuery,
} from '../../hooks/queries/useFacilitiesQuery';
import { useRouteServicesQuery } from '../../hooks/queries/useRouteServicesQuery';
import { useRouteTasksQuery } from '../../hooks/queries/useRouteTasksQuery';

import { RouteTaskCard, ServiceAssetInfo } from './RouteTaskCard';

export interface RouteTasksListProps {
  routeId: string;
  className?: string;
}

export const RouteTasksList = ({ routeId, className }: RouteTasksListProps) => {
  const t = useTranslations();
  const { data, isLoading } = useRouteTasksQuery({
    routeId,
    queryOptions: { enabled: !!routeId },
  });
  const { data: addressesData } = useAccountAddressesQuery({
    limit: 500,
  });
  const { data: facilitiesData } = useFacilitiesQuery({ limit: 500 });
  const { data: servicesData } = useRouteServicesQuery({
    routeId,
    queryOptions: { enabled: !!routeId },
  });

  const tasks = data?.tasks ?? [];

  const addressMap = React.useMemo(() => {
    const map = new Map<string, AccountAddress>();
    for (const addr of addressesData?.addresses ?? []) {
      map.set(addr.id, addr);
    }
    return map;
  }, [addressesData?.addresses]);

  const facilityMap = React.useMemo(() => {
    const map = new Map<string, Facility>();
    for (const f of facilitiesData?.facilities ?? []) {
      map.set(f.id, f);
    }
    return map;
  }, [facilitiesData?.facilities]);

  const serviceAssetMap = React.useMemo(() => {
    const map = new Map<string, ServiceAssetInfo[]>();
    for (const service of servicesData ?? []) {
      const assets: ServiceAssetInfo[] = [];
      const seen = new Set<string>();
      for (const aq of service.assetQuantities ?? []) {
        const key = `${aq.assetType?.id}-${aq.assetGroup}`;
        if (aq.assetType?.name && !seen.has(key)) {
          seen.add(key);
          assets.push({
            assetTypeName: aq.assetType.name,
            assetGroup: aq.assetGroup,
          });
        }
      }
      map.set(service.id, assets);
    }
    return map;
  }, [servicesData, tasks]);

  return (
    <div className={cn('@RouteTasksList flex flex-col gap-2', className)}>
      <Label>{t.routesView.form.tabs.tasks}</Label>
      <Loading isLoading={isLoading}>
        {tasks.length > 0 ? (
          <Col className="gap-2">
            {tasks.map((task) => (
              <RouteTaskCard
                key={task.id}
                task={task}
                addressMap={addressMap}
                facilityMap={facilityMap}
                serviceAssetMap={serviceAssetMap}
              />
            ))}
          </Col>
        ) : (
          <div className="flex items-center justify-center py-8 text-neutral/50">
            {t.routesView.form.services.noTasks}
          </div>
        )}
      </Loading>
    </div>
  );
};
